import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';

/**
 * Guided-tour progress, kept in this browser per signed-in user:
 * { tours: { [tourId]: 'completed' | 'skipped' }, prompted: { [tourId]: true }, welcomeSeen,
 *   checklistHidden, setupComplete: { [platform]: true }, toolsVisited: { [tool]: true } }. A new browser shows the welcome
 * again, which is fine for a tour.
 */
const STORAGE_PREFIX = 'bridgedTours:';
const CHANGE_EVENT = 'bridged-tour-progress';

const EMPTY = {
  tours: {},
  prompted: {},
  welcomeSeen: false,
  checklistHidden: false,
  setupComplete: {},
  toolsVisited: {},
};

const keyFor = userKey => `${STORAGE_PREFIX}${userKey || 'anonymous'}`;

// useSyncExternalStore needs a stable snapshot, so parsed values are cached per raw string.
const cache = new Map();

function read(userKey) {
  let raw = null;
  try {
    raw = localStorage.getItem(keyFor(userKey));
  } catch {
    raw = null;
  }
  const cached = cache.get(userKey);
  if (cached && cached.raw === raw) return cached.value;
  let value = EMPTY;
  try {
    value = raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    value = EMPTY;
  }
  cache.set(userKey, { raw, value });
  return value;
}

function write(userKey, update) {
  const next = update(read(userKey));
  try {
    localStorage.setItem(keyFor(userKey), JSON.stringify(next));
  } catch {
    // Storage blocked (private mode): the tour still works, it just isn't remembered.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(callback) {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

export function markTour(userKey, tourId, status) {
  write(userKey, p => ({ ...p, tours: { ...p.tours, [tourId]: status } }));
}

export function markTourPrompted(userKey, tourId) {
  write(userKey, p => ({ ...p, prompted: { ...p.prompted, [tourId]: true } }));
}

export function markWelcomeSeen(userKey) {
  write(userKey, p => ({ ...p, welcomeSeen: true }));
}

export function setChecklistHidden(userKey, hidden) {
  write(userKey, p => ({ ...p, checklistHidden: hidden }));
}

/** Every setup step is done on this platform: the checklist stops checking from now on. */
export function markSetupComplete(userKey, platform) {
  write(userKey, p => ({ ...p, setupComplete: { ...p.setupComplete, [platform]: true } }));
}

/** The user has opened this create tool before, so it starts plain from now on. */
export function markToolVisited(userKey, tool) {
  write(userKey, p => ({ ...p, toolsVisited: { ...p.toolsVisited, [tool]: true } }));
}

/** Forget every tour, so the welcome and the first-visit prompts come back. */
export function resetTours(userKey) {
  write(userKey, () => EMPTY);
}

/** The signed-in user's key; tours wait until the profile has loaded. */
export function useTourUserKey() {
  return useSelector(state => state.user.data?.email) || null;
}

export function useTourProgress() {
  const userKey = useTourUserKey();
  const progress = useSyncExternalStore(subscribe, () => read(userKey));
  return { userKey, progress };
}

/**
 * Calls `onFirstVisit` once, the first time this user opens a create tool (`tool` is a key
 * such as 'engagement'), then remembers the visit. Later visits, refreshes included, start
 * with the tool's plain defaults. Waits for the profile, since progress is kept per user.
 */
export function useFirstToolVisit(tool, onFirstVisit) {
  const { userKey, progress } = useTourProgress();
  const handledRef = useRef(false);
  const callbackRef = useRef(onFirstVisit);
  callbackRef.current = onFirstVisit;

  useEffect(() => {
    if (handledRef.current || !userKey) return;
    handledRef.current = true;
    if (progress.toolsVisited?.[tool]) return;
    markToolVisited(userKey, tool);
    callbackRef.current();
  }, [userKey, progress, tool]);
}
